import * as ModelEvent from "@node-elion/syncron";
import { uniq } from "lodash";

import SubscriptionPool from "../../redux/services/SubscriptionPool";
import createLogger from "../../utils/logger.util";
import { createObjectLanguageNames } from "../../assets/languages/langs";
import ServiceSubscribeOptionsBase from "../../types/ServiceSubscribeOptionsBase";
import { SortingOrder } from "../../types/SortingOrder";
import SubscriptionBase from "../../types/Subscription";
import { TaxiService, Base, Language } from "..";

const logger = createLogger({ name: "DispatcherRate" });

class DispatcherRate extends Base {
	static fromResponse(
		data: any,
		lang: Language = "uk",
	): DispatcherRate.Model {
		const taxiServices = data?.taxiServices || [];
		const taxiServiceIds = (() => {
			try {
				if (!taxiServices || !taxiServices.length) return [];
				return uniq<number>(taxiServices.map((item: any) => item?.id));
			} catch {
				return [];
			}
		})();

		let userTariffPlans = data?.userTariffPlans;
		if (typeof userTariffPlans === "string") {
			try {
				userTariffPlans = JSON.parse(userTariffPlans);
			} catch {
				userTariffPlans = undefined;
			}
		}

		const dispatcherIds = (() => {
			if (Array.isArray(data?.dispatcherIds)) {
				return data.dispatcherIds;
			}

			if (Array.isArray(data?.dispatchers)) {
				return data.dispatchers
					.map((item: { id?: number }) => item?.id)
					.filter((id: number | undefined): id is number =>
						Number.isFinite(id),
					);
			}

			return [];
		})();

		return {
			id: data?.id,
			active: data?.active ?? true,
			default: data?.default ?? false,
			root: data?.root || false,

			taxiServices,
			taxiServiceIds,
			dispatcherIds,

			name: data?.name?.[lang] || data?.name?.ru || "",

			userTariffPlans: userTariffPlans || undefined,

			updatedAt: data?.updatedAt,
			createdAt: data?.createdAt,
			deletedAt: data?.deletedAt,
		};
	}

	static toRequest(
		model: DispatcherRate.Model.New | DispatcherRate.Model.Modified,
	): any {
		// Очищаем пустые объекты из userTariffPlans
		let { userTariffPlans } = model;
		if (userTariffPlans) {
			const cleaned: any = {};

			const hasCommissionData = (commission: any): boolean => {
				if (!commission || typeof commission !== "object") return false;
				if (!commission.active) return false;
				return (
					(commission.basePercent !== null &&
						commission.basePercent !== undefined &&
						commission.basePercent !== 0) ||
					(commission.baseAmount !== null &&
						commission.baseAmount !== undefined &&
						commission.baseAmount !== 0) ||
					(commission.successPercent !== null &&
						commission.successPercent !== undefined &&
						commission.successPercent !== 0) ||
					(commission.successAmount !== null &&
						commission.successAmount !== undefined &&
						commission.successAmount !== 0)
				);
			};

			if (userTariffPlans.ownOrders) {
				const { ownOrders } = userTariffPlans;
				const hasOwnOrders =
					Object.values(ownOrders).some(hasCommissionData);
				if (hasOwnOrders) {
					cleaned.ownOrders = ownOrders;
				}
			}

			if (userTariffPlans.sentToReassignment) {
				const { sentToReassignment } = userTariffPlans;
				const hasSentToReassignment =
					Object.values(sentToReassignment).some(hasCommissionData);
				if (hasSentToReassignment) {
					cleaned.sentToReassignment = sentToReassignment;
				}
			}

			if (userTariffPlans.acceptedFromReassignment) {
				const { acceptedFromReassignment } = userTariffPlans;
				const hasAcceptedFromReassignment = Object.values(
					acceptedFromReassignment,
				).some(hasCommissionData);
				if (hasAcceptedFromReassignment) {
					cleaned.acceptedFromReassignment = acceptedFromReassignment;
				}
			}

			userTariffPlans =
				Object.keys(cleaned).length > 0 ? cleaned : undefined;
		}

		const payload = {
			active: model.active,
			default: model.default,
			taxiServiceIds: model.taxiServiceIds,
			name: createObjectLanguageNames(model.name),
			userTariffPlans,
			...(Array.isArray(model.dispatcherIds)
				? {
						dispatcherIds: model.dispatcherIds
							.map((id) => Number(id))
							.filter((id) => Number.isInteger(id) && id > 0),
				  }
				: {}),
		};

		return payload;
	}

	public static async getById(id: number, lang?: Language) {
		const data = await this.request((prpc) =>
			prpc.theirsModel.dispatcherRate.getById(id),
		);

		return DispatcherRate.fromResponse(data, lang);
	}

	public static async store(object: DispatcherRate.Model.New, force = false) {
		try {
			const res = await this.request(
				(prpc) =>
					prpc.theirsModel.dispatcherRate.create(
						DispatcherRate.toRequest(object),
						{ force },
					),
				{ silent: false, error: true },
			);
			logger.info("DispatcherRate store", { object, res, force });
			return res;
		} catch (error) {
			return null;
		}
	}

	public static async update(
		object: DispatcherRate.Model.Modified,
		force = false,
	) {
		try {
			const res = await this.request(
				(prpc) =>
					prpc.theirsModel.dispatcherRate.update(
						object.id,
						DispatcherRate.toRequest(object),
						{ force },
					),
				{ silent: false, error: true },
			);
			logger.info("DispatcherRate update", {
				format: DispatcherRate.toRequest(object),
				object,
				res,
				force,
			});
			return res;
		} catch (error) {
			return null;
		}
	}

	public static async destroy(id: number[] | number) {
		if (Array.isArray(id))
			await Promise.all(id.map((id) => this.destroyOne(id)));
		else await this.destroyOne(id);
	}

	public static async subscribe(
		options: DispatcherRate.SubscribeOptions,
		onUpdate: SubscriptionBase.OnUpdate<DispatcherRate.Model>,
	): Promise<SubscriptionBase<DispatcherRate.SubscribeOptions> | null> {
		let currentLang = options.lang || "uk";

		const modelEventConstructor = new ModelEvent.ModelEventConstructor({
			onUpdate: (state) => {
				logger.info("[DispatcherRate] subscribe", {
					options: { ...options, lang: currentLang },
					state,
				});
				onUpdate({
					...state,

					models: state.models.map((item) =>
						this.fromResponse(item, currentLang),
					),
				});
			},
		});
		const subscription = await SubscriptionPool.add(
			(prpc) =>
				prpc.theirsModel.dispatcherRate.subscribe({
					params: this.optionsToRequest(options),
					ping: () => true,
					onEvent: (event) => {
						modelEventConstructor.onEvent(event);
					},
					onError: (error) => {
						logger.error(error);
					},
				}),
			{ name: "DispatcherRate.subscribe" },
		);

		return {
			unsubscribe: () => subscription.unsubscribe(),
			update: (newOptions: DispatcherRate.SubscribeOptions) => {
				currentLang = newOptions.lang || "uk";
				return subscription.update(this.optionsToRequest(newOptions));
			},
		} as SubscriptionBase<DispatcherRate.SubscribeOptions>;
	}

	private static async destroyOne(id: number) {
		this.request((prpc) => prpc.theirsModel.dispatcherRate.delete(id));
	}

	private static optionsToRequest(options: DispatcherRate.SubscribeOptions) {
		return {
			query: options.query,
			offset: options.offset,
			limit: options.limit,

			taxiServiceIds: options.taxiServiceIds,

			order: options.order,
		};
	}
}

declare namespace DispatcherRate {
	export interface Model {
		id: number;
		active: boolean;
		root: boolean;
		default: boolean;

		taxiServices?: TaxiService.Model[];
		taxiServiceIds: number[];
		dispatcherIds: number[];

		name: string;

		userTariffPlans?: {
			ownOrders?: {
				createOrderCommission?: {
					active: boolean;
					basePercent: number | null;
					baseAmount: number | null;
					successPercent: number | null;
					successAmount: number | null;
				};
				sentToExecutionCommission?: {
					active: boolean;
					basePercent: number | null;
					baseAmount: number | null;
					successPercent: number | null;
					successAmount: number | null;
				};
				phoneOrderCommission?: {
					active: boolean;
					basePercent: number | null;
					baseAmount: number | null;
					successPercent: number | null;
					successAmount: number | null;
				};
				closedOrderCommission?: {
					active: boolean;
					basePercent: number | null;
					baseAmount: number | null;
					successPercent: number | null;
					successAmount: number | null;
				};
			};
			sentToReassignment?: {
				sentToExecutionCommission?: {
					active: boolean;
					basePercent: number | null;
					baseAmount: number | null;
					successPercent: number | null;
					successAmount: number | null;
				};
				phoneOrderCommission?: {
					active: boolean;
					basePercent: number | null;
					baseAmount: number | null;
					successPercent: number | null;
					successAmount: number | null;
				};
				closedOrderCommission?: {
					active: boolean;
					basePercent: number | null;
					baseAmount: number | null;
					successPercent: number | null;
					successAmount: number | null;
				};
			};
			acceptedFromReassignment?: {
				sentToExecutionCommission?: {
					active: boolean;
					basePercent: number | null;
					baseAmount: number | null;
					successPercent: number | null;
					successAmount: number | null;
				};
				phoneOrderCommission?: {
					active: boolean;
					basePercent: number | null;
					baseAmount: number | null;
					successPercent: number | null;
					successAmount: number | null;
				};
				closedOrderCommission?: {
					active: boolean;
					basePercent: number | null;
					baseAmount: number | null;
					successPercent: number | null;
					successAmount: number | null;
				};
			};
		};

		updatedAt: string;
		createdAt: string;
		deletedAt: string | null;
	}

	interface SubscribeOptions
		extends Omit<
			ServiceSubscribeOptionsBase<DispatcherRate.Model>,
			"order"
		> {
		taxiServiceIds?: number[];
		lang?: Language;
		order?: Order;
	}

	type Order = Partial<Record<keyof Model, SortingOrder>>;

	namespace Model {
		interface New {
			name: string;

			active: boolean;
			default: boolean;
			taxiServiceIds: number[];
			dispatcherIds?: number[];

			userTariffPlans?: DispatcherRate.Model["userTariffPlans"];
		}
		type Modified = Partial<New> & { id: number };
	}
}

export default DispatcherRate;
